import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { format } from 'date-fns';
import { 
  Car, 
  MapPin,
  Clock,
  Users,
  Plane,
  Building2,
  Plus,
  Edit,
  Loader2,
  RefreshCw,
  Search,
  CheckCircle2,
  XCircle,
  Phone,
  Calendar,
  Navigation,
  Ticket,
  Gift,
  HelpCircle,
  Briefcase,
  AlertTriangle,
  Play,
  UserCheck,
  Compass,
  Mountain,
  Camera,
  Utensils,
  Music
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';

export default function ConciergePage() {
  const { currentProperty } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [transportBookings, setTransportBookings] = useState([]);
  const [activityBookings, setActivityBookings] = useState([]);
  const [activities, setActivities] = useState([]);
  const [conciergeRequests, setConciergeRequests] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  
  // Dialog states
  const [showTransportDialog, setShowTransportDialog] = useState(false);
  const [showActivityBookingDialog, setShowActivityBookingDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showAddActivityDialog, setShowAddActivityDialog] = useState(false);
  
  // Form states
  const [activityForm, setActivityForm] = useState({
    name: '',
    category: '',
    description: '',
    duration_hours: 2,
    adult_price: 0,
    child_price: 0,
    min_participants: 1,
    max_participants: 20,
    meeting_point: '',
    included_items: '',
    booking_deadline_hours: 24,
    cancellation_hours: 24,
    status: 'ACTIVE'
  });
  
  // Form states
  const [transportForm, setTransportForm] = useState({
    guest_name: '',
    guest_phone: '',
    booking_type: 'AIRPORT_PICKUP',
    pickup_date: format(new Date(), 'yyyy-MM-dd'),
    pickup_time: '10:00',
    pickup_location: '',
    dropoff_location: '',
    flight_number: '',
    passenger_count: 1,
    luggage_count: 1,
    special_requirements: '',
    vehicle_type_id: '',
    payment_method: 'ROOM_CHARGE'
  });
  
  const [activityBookingForm, setActivityBookingForm] = useState({
    activity_id: '',
    guest_name: '',
    guest_phone: '',
    booking_date: format(new Date(), 'yyyy-MM-dd'),
    booking_time: '09:00',
    adult_count: 1,
    child_count: 0,
    special_requests: '',
    pickup_required: false,
    pickup_location: '',
    payment_method: 'ROOM_CHARGE'
  });
  
  const [requestForm, setRequestForm] = useState({
    guest_name: '',
    room_number: '',
    request_type: 'RESTAURANT_RESERVATION',
    description: '',
    requested_date: format(new Date(), 'yyyy-MM-dd'),
    requested_time: '',
    priority: 'NORMAL'
  });

  useEffect(() => {
    if (currentProperty?.id) {
      loadDashboard();
      loadVehicleTypes();
      loadActivities();
    }
  }, [currentProperty]);

  useEffect(() => {
    if (currentProperty?.id && activeTab === 'transportation') {
      loadTransportBookings();
      loadVehicles();
      loadDrivers();
    } else if (currentProperty?.id && activeTab === 'activities') {
      loadActivityBookings();
    } else if (currentProperty?.id && activeTab === 'requests') {
      loadConciergeRequests();
    }
  }, [currentProperty, activeTab]);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/concierge/dashboard?property_id=${currentProperty.id}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTransportBookings = async () => {
    try {
      const response = await api.get(`/concierge/transportation?property_id=${currentProperty.id}`);
      setTransportBookings(response.data || []);
    } catch (error) {
      console.error('Error loading transport bookings:', error);
    }
  };

  const loadActivityBookings = async () => {
    try {
      const response = await api.get(`/concierge/activity-bookings?property_id=${currentProperty.id}`);
      setActivityBookings(response.data || []);
    } catch (error) {
      console.error('Error loading activity bookings:', error);
    }
  };

  const loadActivities = async () => {
    try {
      const response = await api.get(`/concierge/activities?property_id=${currentProperty.id}`);
      setActivities(response.data || []);
    } catch (error) {
      console.error('Error loading activities:', error);
    }
  };

  const loadConciergeRequests = async () => {
    try {
      const response = await api.get(`/concierge/requests?property_id=${currentProperty.id}`);
      setConciergeRequests(response.data || []);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  };

  const loadVehicleTypes = async () => {
    try {
      const response = await api.get(`/concierge/vehicle-types?property_id=${currentProperty.id}`);
      setVehicleTypes(response.data || []);
    } catch (error) {
      console.error('Error loading vehicle types:', error);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await api.get(`/concierge/vehicles?property_id=${currentProperty.id}`);
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const loadDrivers = async () => {
    try {
      const response = await api.get(`/concierge/drivers?property_id=${currentProperty.id}`);
      setDrivers(response.data || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  };

  const handleCreateTransportBooking = async () => {
    try {
      await api.post(`/concierge/transportation?property_id=${currentProperty.id}`, transportForm);
      toast.success('Transport booking created');
      setShowTransportDialog(false);
      loadTransportBookings();
      loadDashboard();
      resetTransportForm();
    } catch (error) {
      toast.error('Failed to create booking');
    }
  };

  const handleCreateActivityBooking = async () => {
    try {
      await api.post(`/concierge/activity-bookings?property_id=${currentProperty.id}`, activityBookingForm);
      toast.success('Activity booking created');
      setShowActivityBookingDialog(false);
      loadActivityBookings();
      loadDashboard();
      resetActivityBookingForm();
    } catch (error) {
      toast.error('Failed to create booking');
    }
  };

  const handleCreateRequest = async () => {
    try {
      await api.post(`/concierge/requests?property_id=${currentProperty.id}`, requestForm);
      toast.success('Request created');
      setShowRequestDialog(false);
      loadConciergeRequests();
      loadDashboard();
      resetRequestForm();
    } catch (error) {
      toast.error('Failed to create request');
    }
  };

  const handleCreateActivity = async () => {
    try {
      const payload = {
        ...activityForm,
        duration_hours: parseFloat(activityForm.duration_hours) || 2,
        adult_price: parseFloat(activityForm.adult_price) || 0,
        child_price: parseFloat(activityForm.child_price) || 0,
        min_participants: parseInt(activityForm.min_participants) || 1,
        max_participants: parseInt(activityForm.max_participants) || 20,
        booking_deadline_hours: parseInt(activityForm.booking_deadline_hours) || 24,
        cancellation_hours: parseInt(activityForm.cancellation_hours) || 24
      };
      if (payload.included_items) {
        payload.included_items = payload.included_items.split(',').map(s => s.trim());
      } else {
        payload.included_items = [];
      }
      await api.post(`/concierge/activities?property_id=${currentProperty.id}`, payload);
      toast.success('Activity created successfully');
      setShowAddActivityDialog(false);
      loadActivities();
      setActivityForm({
        name: '', category: '', description: '', duration_hours: 2,
        adult_price: 0, child_price: 0, min_participants: 1, max_participants: 20,
        meeting_point: '', included_items: '', booking_deadline_hours: 24,
        cancellation_hours: 24, status: 'ACTIVE'
      });
    } catch (error) {
      toast.error('Failed to create activity');
      console.error('Error creating activity:', error);
    }
  };

  const handleUpdateTransportStatus = async (bookingId, status) => {
    try {
      await api.patch(`/concierge/transportation/${bookingId}/status?status=${status}`);
      toast.success(`Booking ${status.toLowerCase()}`);
      loadTransportBookings();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateActivityStatus = async (bookingId, status) => {
    try {
      await api.patch(`/concierge/activity-bookings/${bookingId}/status?status=${status}`);
      toast.success(`Booking ${status.toLowerCase()}`);
      loadActivityBookings();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateRequestStatus = async (requestId, status) => {
    try {
      await api.patch(`/concierge/requests/${requestId}?status=${status}`);
      toast.success(`Request ${status.toLowerCase()}`);
      loadConciergeRequests();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetTransportForm = () => {
    setTransportForm({
      guest_name: '',
      guest_phone: '',
      booking_type: 'AIRPORT_PICKUP',
      pickup_date: format(new Date(), 'yyyy-MM-dd'),
      pickup_time: '10:00',
      pickup_location: '',
      dropoff_location: '',
      flight_number: '',
      passenger_count: 1,
      luggage_count: 1,
      special_requirements: '',
      vehicle_type_id: '',
      payment_method: 'ROOM_CHARGE'
    });
  };

  const resetActivityBookingForm = () => {
    setActivityBookingForm({
      activity_id: '',
      guest_name: '',
      guest_phone: '',
      booking_date: format(new Date(), 'yyyy-MM-dd'),
      booking_time: '09:00',
      adult_count: 1,
      child_count: 0,
      special_requests: '',
      pickup_required: false,
      pickup_location: '',
      payment_method: 'ROOM_CHARGE'
    });
  };

  const resetRequestForm = () => {
    setRequestForm({
      guest_name: '',
      room_number: '',
      request_type: 'RESTAURANT_RESERVATION',
      description: '',
      requested_date: format(new Date(), 'yyyy-MM-dd'),
      requested_time: '',
      priority: 'NORMAL'
    });
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'CONFIRMED': case 'DRIVER_ASSIGNED': case 'CHECKED_IN': return 'default';
      case 'IN_PROGRESS': return 'secondary';
      case 'COMPLETED': return 'outline';
      case 'CANCELLED': case 'NO_SHOW': return 'destructive';
      default: return 'secondary';
    }
  };

  const getBookingTypeLabel = (type) => {
    const labels = {
      AIRPORT_PICKUP: 'Airport Pickup',
      AIRPORT_DROPOFF: 'Airport Drop-off',
      CITY_TOUR: 'City Tour',
      POINT_TO_POINT: 'Point to Point',
      HOURLY_HIRE: 'Hourly Hire'
    };
    return labels[type] || type;
  };

  const getRequestTypeLabel = (type) => {
    const labels = {
      HOUSEKEEPING: 'Housekeeping',
      MAINTENANCE: 'Maintenance',
      ROOM_SERVICE: 'Room Service',
      CONCIERGE: 'Concierge',
      TRANSPORT: 'Transport',
      WAKE_UP: 'Wake-up Call',
      RESTAURANT_RESERVATION: 'Restaurant',
      TICKET_BOOKING: 'Tickets',
      FLOWER_DELIVERY: 'Flowers',
      GIFT_ARRANGEMENT: 'Gift',
      SPECIAL_OCCASION: 'Special Occasion',
      INFORMATION: 'Information',
      LOST_FOUND: 'Lost & Found',
      LUGGAGE: 'Luggage',
      OTHER: 'Other'
    };
    return labels[type] || type;
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'URGENT': return 'destructive';
      case 'HIGH': return 'default';
      case 'NORMAL': return 'secondary';
      default: return 'outline';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (!currentProperty) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="concierge-no-property">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No Property Selected</h3>
          <p className="text-muted-foreground">Please select a property</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="concierge-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="concierge-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Concierge Services</h1>
          <p className="text-muted-foreground">Transportation, tours, and guest services</p>
        </div>
        <Button onClick={loadDashboard} variant="outline" data-testid="refresh-concierge-btn">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-concierge-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="transportation" data-testid="tab-transportation">Transportation</TabsTrigger>
          <TabsTrigger value="activities" data-testid="tab-activities">Tours & Activities</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Guest Requests</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="stat-transfers-today">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Today's Transfers</CardTitle>
                <Car className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.today?.transfers || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboardData?.today?.pending_transfers || 0} pending, {dashboardData?.today?.active_transfers || 0} active
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-activities-today">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Today's Activities</CardTitle>
                <Compass className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.today?.activities || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboardData?.today?.confirmed_activities || 0} confirmed
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-pending-requests">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Pending Requests</CardTitle>
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.requests?.pending || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboardData?.requests?.urgent || 0} urgent
                </p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-available-resources">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Available Resources</CardTitle>
                <UserCheck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dashboardData?.resources?.vehicles_available || 0} / {dashboardData?.resources?.drivers_available || 0}
                </div>
                <p className="text-xs text-muted-foreground">Vehicles / Drivers</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => { setActiveTab('transportation'); setShowTransportDialog(true); }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <Car className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Book Transfer</h3>
                    <p className="text-sm text-muted-foreground">Airport or city transfers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => { setActiveTab('activities'); setShowActivityBookingDialog(true); }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <Mountain className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Book Activity</h3>
                    <p className="text-sm text-muted-foreground">Tours and experiences</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => { setActiveTab('requests'); setShowRequestDialog(true); }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                    <HelpCircle className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">New Request</h3>
                    <p className="text-sm text-muted-foreground">Guest service request</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Transportation Tab */}
        <TabsContent value="transportation" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Transportation Bookings</CardTitle>
                  <CardDescription>Manage airport transfers and vehicle bookings</CardDescription>
                </div>
                <Button onClick={() => setShowTransportDialog(true)} data-testid="add-transport-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  New Booking
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking #</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transportBookings.map((booking) => (
                    <TableRow key={booking.id} data-testid={`transport-booking-${booking.id}`}>
                      <TableCell className="font-medium">{booking.booking_number}</TableCell>
                      <TableCell>
                        <div>{booking.guest_name}</div>
                        <div className="text-sm text-muted-foreground">{booking.guest_phone}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getBookingTypeLabel(booking.booking_type)}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>{booking.pickup_date}</div>
                        <div className="text-sm text-muted-foreground">{booking.pickup_time}</div>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <div className="truncate">{booking.pickup_location}</div>
                        {booking.dropoff_location && (
                          <div className="text-sm text-muted-foreground truncate">→ {booking.dropoff_location}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(booking.status)}>
                          {booking.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {booking.status === 'PENDING' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUpdateTransportStatus(booking.id, 'CONFIRMED')}
                            >
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                          {booking.status === 'CONFIRMED' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUpdateTransportStatus(booking.id, 'IN_PROGRESS')}
                            >
                              <Play className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}
                          {booking.status === 'IN_PROGRESS' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUpdateTransportStatus(booking.id, 'COMPLETED')}
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {transportBookings.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No transportation bookings
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activities Tab */}
        <TabsContent value="activities" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Activity Bookings</CardTitle>
                  <CardDescription>Tours, excursions, and guest experiences</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowAddActivityDialog(true)} data-testid="add-activity-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Activity
                  </Button>
                  <Button onClick={() => setShowActivityBookingDialog(true)} data-testid="add-activity-booking-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    New Booking
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking #</TableHead>
                    <TableHead>Activity</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Participants</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityBookings.map((booking) => (
                    <TableRow key={booking.id} data-testid={`activity-booking-${booking.id}`}>
                      <TableCell className="font-medium">{booking.booking_number}</TableCell>
                      <TableCell>{booking.activities?.name || '-'}</TableCell>
                      <TableCell>
                        <div>{booking.guest_name}</div>
                        <div className="text-sm text-muted-foreground">{booking.guest_phone}</div>
                      </TableCell>
                      <TableCell>
                        <div>{booking.booking_date}</div>
                        <div className="text-sm text-muted-foreground">{booking.booking_time}</div>
                      </TableCell>
                      <TableCell>
                        {booking.adult_count} adults
                        {booking.child_count > 0 && `, ${booking.child_count} children`}
                      </TableCell>
                      <TableCell>{formatCurrency(booking.total_amount)}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(booking.status)}>
                          {booking.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {booking.status === 'PENDING' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUpdateActivityStatus(booking.id, 'CONFIRMED')}
                            >
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                          {booking.status === 'CONFIRMED' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUpdateActivityStatus(booking.id, 'CHECKED_IN')}
                            >
                              <UserCheck className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {activityBookings.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No activity bookings
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Requests Tab */}
        <TabsContent value="requests" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Guest Requests</CardTitle>
                  <CardDescription>Concierge service requests</CardDescription>
                </div>
                <Button onClick={() => setShowRequestDialog(true)} data-testid="add-request-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  New Request
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request #</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {conciergeRequests.map((request) => (
                    <TableRow key={request.id} data-testid={`concierge-request-${request.id}`}>
                      <TableCell className="font-medium">{request.request_number}</TableCell>
                      <TableCell>
                        <div>{request.title || request.guest_name}</div>
                        {request.room_number && (
                          <div className="text-sm text-muted-foreground">Room {request.room_number}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getRequestTypeLabel(request.request_type || request.category)}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[250px] truncate">{request.description || request.title}</TableCell>
                      <TableCell>
                        <Badge variant={getPriorityBadgeVariant(request.priority)}>
                          {request.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(request.status)}>
                          {request.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {request.status === 'PENDING' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUpdateRequestStatus(request.id, 'IN_PROGRESS')}
                            >
                              <Play className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}
                          {request.status === 'IN_PROGRESS' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUpdateRequestStatus(request.id, 'COMPLETED')}
                            >
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {conciergeRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No guest requests
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Transport Booking Dialog */}
      <Dialog open={showTransportDialog} onOpenChange={setShowTransportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Transport Booking</DialogTitle>
            <DialogDescription>Book a transfer or vehicle</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guest Name *</Label>
                <Input
                  value={transportForm.guest_name}
                  onChange={(e) => setTransportForm({...transportForm, guest_name: e.target.value})}
                  data-testid="input-transport-guest"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={transportForm.guest_phone}
                  onChange={(e) => setTransportForm({...transportForm, guest_phone: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Booking Type *</Label>
              <Select value={transportForm.booking_type} onValueChange={(v) => setTransportForm({...transportForm, booking_type: v})}>
                <SelectTrigger data-testid="select-booking-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AIRPORT_PICKUP">Airport Pickup</SelectItem>
                  <SelectItem value="AIRPORT_DROPOFF">Airport Drop-off</SelectItem>
                  <SelectItem value="CITY_TOUR">City Tour</SelectItem>
                  <SelectItem value="POINT_TO_POINT">Point to Point</SelectItem>
                  <SelectItem value="HOURLY_HIRE">Hourly Hire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pickup Date *</Label>
                <Input
                  type="date"
                  value={transportForm.pickup_date}
                  onChange={(e) => setTransportForm({...transportForm, pickup_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Pickup Time *</Label>
                <Input
                  type="time"
                  value={transportForm.pickup_time}
                  onChange={(e) => setTransportForm({...transportForm, pickup_time: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pickup Location *</Label>
              <Input
                value={transportForm.pickup_location}
                onChange={(e) => setTransportForm({...transportForm, pickup_location: e.target.value})}
                placeholder="e.g., Bole International Airport"
              />
            </div>
            <div className="space-y-2">
              <Label>Drop-off Location</Label>
              <Input
                value={transportForm.dropoff_location}
                onChange={(e) => setTransportForm({...transportForm, dropoff_location: e.target.value})}
                placeholder="e.g., Hotel or destination"
              />
            </div>
            {(transportForm.booking_type === 'AIRPORT_PICKUP' || transportForm.booking_type === 'AIRPORT_DROPOFF') && (
              <div className="space-y-2">
                <Label>Flight Number</Label>
                <Input
                  value={transportForm.flight_number}
                  onChange={(e) => setTransportForm({...transportForm, flight_number: e.target.value})}
                  placeholder="e.g., ET302"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Passengers</Label>
                <Input
                  type="number"
                  min="1"
                  value={transportForm.passenger_count}
                  onChange={(e) => setTransportForm({...transportForm, passenger_count: parseInt(e.target.value) || 1})}
                />
              </div>
              <div className="space-y-2">
                <Label>Luggage</Label>
                <Input
                  type="number"
                  min="0"
                  value={transportForm.luggage_count}
                  onChange={(e) => setTransportForm({...transportForm, luggage_count: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Special Requirements</Label>
              <Textarea
                value={transportForm.special_requirements}
                onChange={(e) => setTransportForm({...transportForm, special_requirements: e.target.value})}
                placeholder="Any special needs..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransportDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTransportBooking} data-testid="submit-transport-btn">Create Booking</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Booking Dialog */}
      <Dialog open={showActivityBookingDialog} onOpenChange={setShowActivityBookingDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Book Activity</DialogTitle>
            <DialogDescription>Book a tour or experience</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Activity *</Label>
              <Select value={activityBookingForm.activity_id} onValueChange={(v) => setActivityBookingForm({...activityBookingForm, activity_id: v})}>
                <SelectTrigger data-testid="select-activity">
                  <SelectValue placeholder="Select an activity" />
                </SelectTrigger>
                <SelectContent>
                  {activities.map((activity) => (
                    <SelectItem key={activity.id} value={activity.id}>
                      {activity.name} - {formatCurrency(activity.adult_price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guest Name *</Label>
                <Input
                  value={activityBookingForm.guest_name}
                  onChange={(e) => setActivityBookingForm({...activityBookingForm, guest_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={activityBookingForm.guest_phone}
                  onChange={(e) => setActivityBookingForm({...activityBookingForm, guest_phone: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={activityBookingForm.booking_date}
                  onChange={(e) => setActivityBookingForm({...activityBookingForm, booking_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Time *</Label>
                <Input
                  type="time"
                  value={activityBookingForm.booking_time}
                  onChange={(e) => setActivityBookingForm({...activityBookingForm, booking_time: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adults</Label>
                <Input
                  type="number"
                  min="1"
                  value={activityBookingForm.adult_count}
                  onChange={(e) => setActivityBookingForm({...activityBookingForm, adult_count: parseInt(e.target.value) || 1})}
                />
              </div>
              <div className="space-y-2">
                <Label>Children</Label>
                <Input
                  type="number"
                  min="0"
                  value={activityBookingForm.child_count}
                  onChange={(e) => setActivityBookingForm({...activityBookingForm, child_count: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Special Requests</Label>
              <Textarea
                value={activityBookingForm.special_requests}
                onChange={(e) => setActivityBookingForm({...activityBookingForm, special_requests: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivityBookingDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateActivityBooking} data-testid="submit-activity-booking-btn">Book Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Guest Request</DialogTitle>
            <DialogDescription>Create a concierge service request</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guest Name *</Label>
                <Input
                  value={requestForm.guest_name}
                  onChange={(e) => setRequestForm({...requestForm, guest_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Room Number</Label>
                <Input
                  value={requestForm.room_number}
                  onChange={(e) => setRequestForm({...requestForm, room_number: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Request Type *</Label>
              <Select value={requestForm.request_type} onValueChange={(v) => setRequestForm({...requestForm, request_type: v})}>
                <SelectTrigger data-testid="select-request-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HOUSEKEEPING">Housekeeping</SelectItem>
                  <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                  <SelectItem value="ROOM_SERVICE">Room Service</SelectItem>
                  <SelectItem value="WAKE_UP">Wake-up Call</SelectItem>
                  <SelectItem value="TRANSPORT">Transport</SelectItem>
                  <SelectItem value="CONCIERGE">Concierge</SelectItem>
                  <SelectItem value="RESTAURANT_RESERVATION">Restaurant Reservation</SelectItem>
                  <SelectItem value="TICKET_BOOKING">Ticket Booking</SelectItem>
                  <SelectItem value="FLOWER_DELIVERY">Flower Delivery</SelectItem>
                  <SelectItem value="GIFT_ARRANGEMENT">Gift Arrangement</SelectItem>
                  <SelectItem value="SPECIAL_OCCASION">Special Occasion</SelectItem>
                  <SelectItem value="INFORMATION">Information Request</SelectItem>
                  <SelectItem value="LOST_FOUND">Lost & Found</SelectItem>
                  <SelectItem value="LUGGAGE">Luggage Service</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={requestForm.priority} onValueChange={(v) => setRequestForm({...requestForm, priority: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={requestForm.description}
                onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                placeholder="Describe the request in detail..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRequest} data-testid="submit-request-btn">Create Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Activity Dialog */}
      <Dialog open={showAddActivityDialog} onOpenChange={setShowAddActivityDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-activity-dialog">
          <DialogHeader>
            <DialogTitle>Add Activity</DialogTitle>
            <DialogDescription>Create a new tour, excursion, or experience for guests</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Activity Name *</Label>
                <Input
                  value={activityForm.name}
                  onChange={(e) => setActivityForm({...activityForm, name: e.target.value})}
                  placeholder="e.g., City Walking Tour"
                  data-testid="input-activity-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={activityForm.category} onValueChange={(v) => setActivityForm({...activityForm, category: v})}>
                  <SelectTrigger data-testid="select-activity-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CITY_TOUR">City Tour</SelectItem>
                    <SelectItem value="CULTURAL">Cultural Experience</SelectItem>
                    <SelectItem value="ADVENTURE">Adventure</SelectItem>
                    <SelectItem value="NATURE">Nature / Wildlife</SelectItem>
                    <SelectItem value="FOOD_WINE">Food & Wine</SelectItem>
                    <SelectItem value="WELLNESS">Wellness</SelectItem>
                    <SelectItem value="SPORTS">Sports & Recreation</SelectItem>
                    <SelectItem value="WORKSHOP">Workshop / Class</SelectItem>
                    <SelectItem value="DAY_TRIP">Day Trip</SelectItem>
                    <SelectItem value="NIGHTLIFE">Nightlife</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={activityForm.description}
                onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                placeholder="Describe the activity experience..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Duration (hours)</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={activityForm.duration_hours}
                  onChange={(e) => setActivityForm({...activityForm, duration_hours: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Adult Price (ETB) *</Label>
                <Input
                  type="number"
                  value={activityForm.adult_price}
                  onChange={(e) => setActivityForm({...activityForm, adult_price: e.target.value})}
                  data-testid="input-activity-adult-price"
                />
              </div>
              <div className="space-y-2">
                <Label>Child Price (ETB)</Label>
                <Input
                  type="number"
                  value={activityForm.child_price}
                  onChange={(e) => setActivityForm({...activityForm, child_price: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Participants</Label>
                <Input
                  type="number"
                  value={activityForm.min_participants}
                  onChange={(e) => setActivityForm({...activityForm, min_participants: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Participants</Label>
                <Input
                  type="number"
                  value={activityForm.max_participants}
                  onChange={(e) => setActivityForm({...activityForm, max_participants: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Meeting Point</Label>
              <Input
                value={activityForm.meeting_point}
                onChange={(e) => setActivityForm({...activityForm, meeting_point: e.target.value})}
                placeholder="e.g., Hotel Lobby"
              />
            </div>
            <div className="space-y-2">
              <Label>Included Items</Label>
              <Input
                value={activityForm.included_items}
                onChange={(e) => setActivityForm({...activityForm, included_items: e.target.value})}
                placeholder="e.g., Transport, Guide, Lunch (comma separated)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddActivityDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateActivity} disabled={!activityForm.name || !activityForm.category} data-testid="submit-activity-btn">Create Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
